import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {ScrollRestoration} */
  #pageScrollRestoration = 'auto';

  /** @type {MutationObserver | null} */
  #giftObserver = null;

  /** @type {boolean} */
  #hadGiftSelector = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    // A free gift that becomes claimable mid-session (e.g. after bumping a
    // quantity) is offered at the top of the items list; bring the shopper
    // back up so they see it rather than leaving it scrolled out of view.
    this.#hadGiftSelector = Boolean(this.querySelector(GIFT_SELECTOR));
    this.#giftObserver = new MutationObserver(() => {
      const hasGiftSelector = Boolean(this.querySelector(GIFT_SELECTOR));
      if (hasGiftSelector && !this.#hadGiftSelector) scrollCartToGift(this);
      this.#hadGiftSelector = hasGiftSelector;
    });
    this.#giftObserver.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#giftObserver?.disconnect();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      // scrollRestoration is stored per history entry. The page's own entry
      // saves its scroll position as we push, while the body is locked
      // (position: fixed), so it records 0; going back to it would scroll the
      // page to the top. Turn restoring off on that entry first; it's turned
      // back on once the drawer has closed (#restorePageScroll).
      this.#pageScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  /**
   * When the dialog closes via a normal UI interaction (X button, clicking
   * outside, Escape key), the base DialogComponent has already correctly
   * closed the dialog and restored scroll position (it captures scrollY
   * before ever locking the body, so that mechanism is reliable).
   *
   * We still need to actually remove the history entry #handleHistoryOpen
   * pushed, not just null out its state: replaceState only overwrites the
   * *current* entry in place, it can't shrink the stack. Left in place,
   * that extra same-URL entry sat permanently between the page and
   * wherever the user came from, so the next real back-button press just
   * landed back on this same page (no visible change) and had to be
   * pressed a second time to actually go anywhere - reported as "the back
   * button needs two presses after closing the cart".
   *
   * history.back() is what actually collapses it. We abort our own
   * popstate listener first so that programmatic back doesn't loop back
   * into #handlePopState and re-run dialog-close logic on an already-
   * closed dialog.
   */
  #handleHistoryClose = () => {
    const wasOpenedByUs = history.state?.cartDrawerOpen;

    this.#historyAbortController?.abort();
    this.#historyAbortController = null;

    if (wasOpenedByUs) {
      // The base class has already put the page back where it was; keep it
      // there once we're back on the page's entry.
      const scrollY = window.scrollY;
      window.addEventListener('popstate', () => this.#restorePageScroll(scrollY), { once: true });
      history.back();
    }
  };

  /**
   * Back on the page's own history entry after the drawer closed: hold the
   * scroll position, then hand scroll restoring back to the browser once its
   * own restore moment has passed.
   * @param {number} [scrollY]
   */
  #restorePageScroll(scrollY) {
    if (typeof scrollY === 'number') window.scrollTo({ top: scrollY, behavior: 'instant' });
    const restoration = this.#pageScrollRestoration;
    setTimeout(() => {
      history.scrollRestoration = restoration;
    }, 500);
  }

  /**
   * Only reached via an actual browser/gesture back navigation while the
   * dialog is open (e.g. user presses the hardware back button). In this
   * case we just close the dialog; the base class's own scroll restore
   * handles putting the page back where it was.
   */
  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
    this.#restorePageScroll();
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

const GIFT_SELECTOR = 'cart-gift-selector-component';

/**
 * Scrolls the cart back up to the free gift picker. In the drawer that's the
 * top of its scroll areas; on the cart page, the picker itself.
 * @param {Element} [scope]
 */
function scrollCartToGift(scope) {
  const gift = (scope ?? document).querySelector(GIFT_SELECTOR);
  if (!gift) return;

  const drawer = gift.closest('.cart-drawer__dialog');
  if (drawer) {
    drawer.querySelectorAll('.cart-drawer__items, .cart-drawer__content').forEach((el) => {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    });
  } else {
    gift.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

// While a gift is unclaimed the checkout button reads "Choose Free Gift" and
// takes the shopper to the picker instead of checking out. Captured at the
// document so the form submit and the upsell modal's checkout hook never see it.
if (!window.__cartGiftChooseBound) {
  window.__cartGiftChooseBound = true;
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element && event.target.closest('[data-cart-gift-choose]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      scrollCartToGift(button.closest('.cart-drawer__dialog') ?? document);
    },
    true
  );
}

// The chevron beside the volume-discount headline folds the tier bar away.
// The state is a class on <html> rather than on the panel, because the cart
// re-renders after every change. Below the top tier the bar starts open and
// the shopper's choice is remembered for the browsing session. At the top
// tier (.cart-volume--top) the bar starts folded; opening it lasts until the
// page reloads.
const VOLUME_COLLAPSED_CLASS = 'cart-volume-collapsed';
const VOLUME_COLLAPSED_KEY = 'cart-volume-collapsed';
const VOLUME_TOP_OPEN_CLASS = 'cart-volume-top-open';

function isVolumeBarOpen(button) {
  const classes = document.documentElement.classList;
  if (button.closest('.cart-volume--top')) return classes.contains(VOLUME_TOP_OPEN_CLASS);
  return !classes.contains(VOLUME_COLLAPSED_CLASS);
}

function syncVolumeToggles() {
  document.querySelectorAll('[data-cart-volume-toggle]').forEach((button) => {
    const expanded = String(isVolumeBarOpen(button));
    if (button.getAttribute('aria-expanded') !== expanded) button.setAttribute('aria-expanded', expanded);
  });
}

if (!window.__cartVolumeToggleBound) {
  window.__cartVolumeToggleBound = true;

  try {
    if (sessionStorage.getItem(VOLUME_COLLAPSED_KEY) === 'true') {
      document.documentElement.classList.add(VOLUME_COLLAPSED_CLASS);
      syncVolumeToggles();
    }
  } catch {
    // Storage unavailable (private mode etc.): just start expanded.
  }

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element && event.target.closest('[data-cart-volume-toggle]');
    if (!button) return;
    if (button.closest('.cart-volume--top')) {
      document.documentElement.classList.toggle(VOLUME_TOP_OPEN_CLASS);
    } else {
      const collapsed = document.documentElement.classList.toggle(VOLUME_COLLAPSED_CLASS);
      try {
        sessionStorage.setItem(VOLUME_COLLAPSED_KEY, String(collapsed));
      } catch {
        // Ignore: the toggle still works for this page.
      }
    }
    syncVolumeToggles();
  });

  // Re-rendered toggles come back with the server's default aria-expanded;
  // correct them to match the current state.
  new MutationObserver(syncVolumeToggles).observe(document.body, { childList: true, subtree: true });
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}